// vital_sign schema + Bedrock tool (PLAN.md §6.1 Batch 2). One record per reading;
// the shape varies by `kind`. Pure data — safe for both the API and the web SPA.

export const VITAL_KINDS = [
  "bp",
  "hr",
  "temp",
  "weight",
  "spo2",
  "glucose",
  "respiratory_rate",
] as const;
export type VitalKind = (typeof VITAL_KINDS)[number];

/** Default unit per kind. temp/weight/glucose carry user-selectable units. */
export const VITAL_DEFAULT_UNIT: Record<VitalKind, string> = {
  bp: "mmHg",
  hr: "bpm",
  temp: "°F",
  weight: "lb",
  spo2: "%",
  glucose: "mg/dL",
  respiratory_rate: "breaths/min",
};

export const BP_POSITIONS = ["sitting", "standing", "lying"] as const;
export const BP_ARMS = ["left", "right"] as const;
export const TEMP_METHODS = ["oral", "temporal", "tympanic", "axillary", "rectal"] as const;
export const HR_CONTEXTS = ["resting", "post-exertion", "during-symptom"] as const;
export const GLUCOSE_STATES = ["fasting", "postprandial", "random", "pre-meal", "2h-post-meal"] as const;

/** Payload for a `vital_sign` event (PLAN.md §6.1 Batch 2). */
export interface VitalSignPayload {
  kind: VitalKind; // R
  measuredAt: string; // R (auto-now per D-10)
  unit: string; // R (defaulted server-side per kind)
  value?: number; // R for non-bp kinds
  systolic?: number; // R for bp
  diastolic?: number; // R for bp
  position?: (typeof BP_POSITIONS)[number]; // bp
  arm?: (typeof BP_ARMS)[number]; // bp
  method?: (typeof TEMP_METHODS)[number]; // temp
  context?: (typeof HR_CONTEXTS)[number]; // hr / respiratory_rate
  state?: (typeof GLUCOSE_STATES)[number]; // glucose
  device?: string;
  notes?: string;
}

/**
 * Bedrock tool: record a vital-sign reading. For blood pressure pass `systolic`
 * and `diastolic`; for every other kind pass `value`. Infer the unit sensibly
 * (e.g. "130 over 80" → mmHg) and let the server default it. Omit measuredAt
 * unless the user stated a time (D-10).
 */
export const RECORD_VITAL_TOOL = {
  name: "record_vital_sign",
  description:
    "Record a vital-sign reading: blood pressure (bp), heart rate (hr), temperature (temp), weight, " +
    "blood oxygen (spo2), blood glucose (glucose), or respiratory_rate. For bp pass systolic AND " +
    "diastolic; for all other kinds pass value. Omit measuredAt unless the user stated a time.",
  input_schema: {
    type: "object",
    properties: {
      kind: { type: "string", enum: [...VITAL_KINDS] },
      measuredAt: { type: "string", description: "ISO-8601 with UTC offset. Omit if no time was given." },
      value: { type: "number", description: "The reading (all kinds except bp)." },
      systolic: { type: "number", description: "Systolic (bp only)." },
      diastolic: { type: "number", description: "Diastolic (bp only)." },
      unit: {
        type: "string",
        description: "e.g. mmHg, bpm, °F, °C, lb, kg, %, mg/dL, mmol/L, breaths/min. Server defaults if omitted.",
      },
      position: { type: "string", enum: [...BP_POSITIONS], description: "bp position." },
      arm: { type: "string", enum: [...BP_ARMS], description: "bp arm." },
      method: { type: "string", enum: [...TEMP_METHODS], description: "temp method." },
      context: { type: "string", enum: [...HR_CONTEXTS], description: "hr / respiratory context." },
      state: { type: "string", enum: [...GLUCOSE_STATES], description: "glucose state." },
      device: { type: "string", description: "e.g. 'Omron BP728', 'Apple Watch'." },
      notes: { type: "string" },
    },
    required: ["kind"],
  },
} as const;
